import { useMutation, useQuery } from '@apollo/client'
import { IMPORTAR_GRUPO_USUARIOS } from '../data/migracao-mutation'
import { useCallback } from 'react'
import { SINCRONIZAR_DEALERS } from '@/modules/super-admin/data/sincronizacao-dealers'
import { SINCRONIZAR_GRUPOS } from '@/modules/super-admin/data/sincronizacao-grupos'
import { SINCRONIZAR_IDENTITY_PROVIDER } from '@/modules/super-admin/data/sincronizacao-identity-provider'
import { SINCRONIZAR_USUARIOS } from '@/modules/super-admin/data/sincronizacao-usuarios'
import {
  EditarProdutoMutationMutation,
  EditarProdutoMutationMutationVariables,
  ImportarGrupoUsuariosMutationMutation,
  ImportarGrupoUsuariosMutationMutationVariables,
  ProdutosQueryQuery,
  ProdutosQueryQueryVariables,
  SincronizarDealersMutationMutation,
  SincronizarDealersMutationMutationVariables,
  SincronizarGruposMutationMutation,
  SincronizarGruposMutationMutationVariables,
  SincronizarIdentityProviderMutation,
  SincronizarIdentityProviderMutationVariables,
  SincronizarUsuariosMutationMutation,
  SincronizarUsuariosMutationMutationVariables
} from '@/core/types/graphql'
import { useConfirmModal } from '@/core/context/confirm-modal-context'

import { t } from '@lingui/core/macro'
import {
  EDITAR_PRODUTO,
  PRODUTOS_QUERY
} from '@/modules/super-admin/data/produtos-query'
import { getLabel } from '@/core/utils/global-labels'

export const useSuperAdminQueryHandler = () => {
  const { askUser } = useConfirmModal()
  const { data } = useQuery<ProdutosQueryQuery, ProdutosQueryQueryVariables>(
    PRODUTOS_QUERY
  )

  const [importarGrupoUsuarios] = useMutation<
    ImportarGrupoUsuariosMutationMutation,
    ImportarGrupoUsuariosMutationMutationVariables
  >(IMPORTAR_GRUPO_USUARIOS)

  const [sincronizarGrupos] = useMutation<
    SincronizarGruposMutationMutation,
    SincronizarGruposMutationMutationVariables
  >(SINCRONIZAR_GRUPOS)
  const [sincronizarDealers] = useMutation<
    SincronizarDealersMutationMutation,
    SincronizarDealersMutationMutationVariables
  >(SINCRONIZAR_DEALERS)
  const [sincronizarUsuarios] = useMutation<
    SincronizarUsuariosMutationMutation,
    SincronizarUsuariosMutationMutationVariables
  >(SINCRONIZAR_USUARIOS)
  const [sincronizarIdentityProvider] = useMutation<
    SincronizarIdentityProviderMutation,
    SincronizarIdentityProviderMutationVariables
  >(SINCRONIZAR_IDENTITY_PROVIDER)

  const [editarProduto] = useMutation<
    EditarProdutoMutationMutation,
    EditarProdutoMutationMutationVariables
  >(EDITAR_PRODUTO)

  const handleImportarGrupoUsuariosWithVaribles = useCallback(
    (produtoId: string, baseUriHomolog: string) => {
      askUser({
        onConfirm: async () => {
          try {
            await editarProduto({
              variables: {
                produto: {
                  id: produtoId,
                  baseUriHomolog
                }
              }
            })
          } catch (error) {
            console.error('Erro ao atualizar:', error)
            throw error
          }
        },
        title: t`Você realmente deseja atualizar?`,
        confirmLabel: t`Atualizar`,
        successMessage: t`Atualizado com sucesso!`,
        errorMessage: t`Não foi possível atualizar`
      })
    },
    [editarProduto, askUser]
  )

  const handleEditarProduto = useCallback(
    (produtoId: string, baseUriHomolog: string) => {
      handleImportarGrupoUsuariosWithVaribles(produtoId, baseUriHomolog)
    },
    [handleImportarGrupoUsuariosWithVaribles]
  )

  const handleImportarGrupoUsuarios = useCallback(() => {
    askUser({
      onConfirm: async () => {
        try {
          await importarGrupoUsuarios({ variables: {} })
        } catch (error) {
          console.error('Erro ao migrar grupo de usuários:', error)
          throw error
        }
      },
      title: t`Quer migrar o grupo de usuários?`,
      confirmLabel: t`Migrar`,
      successMessage: t`Migrado com sucesso!`,
      errorMessage: t`Não foi possível migrar o grupo`
    })
  }, [importarGrupoUsuarios, askUser])

  const handleSincronizarGrupos = useCallback(() => {
    askUser({
      onConfirm: async () => {
        try {
          await sincronizarGrupos({ variables: {} })
        } catch (error) {
          console.error(
            `Erro ao sincronizar ${getLabel('label.grupos')}:`,
            error
          )
          throw error
        }
      },
      title: t`Quer sincronizar ${getLabel('label.grupos')}?`,
      confirmLabel: t`Sincronizar`,
      successMessage: t`Sincronizado com sucesso!`,
      errorMessage: t`Não foi possível sincronizar ${getLabel('label.grupos')}`
    })
  }, [sincronizarGrupos, askUser])

  const handleSincronizarUsuarios = useCallback(() => {
    askUser({
      onConfirm: async () => {
        try {
          await sincronizarUsuarios({ variables: {} })
        } catch (error) {
          console.error('Erro ao sincronizar usuários:', error)
          throw error
        }
      },
      title: t`Quer sincronizar usuários?`,
      confirmLabel: t`Sincronizar`,
      successMessage: t`Sincronizado com sucesso!`,
      errorMessage: t`Não foi possível sincronizar os usuários`
    })
  }, [sincronizarUsuarios, askUser])

  const handleSincronizarDealers = useCallback(() => {
    askUser({
      onConfirm: async () => {
        try {
          await sincronizarDealers({ variables: {} })
        } catch (error) {
          console.error(
            `Erro ao sincronizar ${getLabel('label.dealers')}:`,
            error
          )
          throw error
        }
      },
      title: t`Quer sincronizar ${getLabel('label.dealers')}?`,
      confirmLabel: t`Sincronizar`,
      successMessage: t`Sincronizado com sucesso!`,
      errorMessage: t`Não foi possível sincronizar ${getLabel('label.dealers')}`
    })
  }, [sincronizarDealers, askUser])

  return {
    handleImportarGrupoUsuarios,
    handleSincronizarGrupos,
    handleSincronizarUsuarios,
    handleSincronizarDealers,
    sincronizarIdentityProvider,
    produtos: data?.produtos ?? [],
    handleEditarProduto
  }
}
